/**
 * Identity auth for the FPA SPA — a thin, typed adapter over `@rbi/auth-web` (the shared browser
 * auth SDK), mirroring los-frontend's `identityAuth.ts`. The SDK owns the security logic (Hosted-UI
 * SSO with PKCE + state, refresh, per-app logout); this module only keeps the tokens and wires the
 * API client seams. Nothing outside `src/features/auth/` imports `@rbi/auth-web`.
 *
 * TOKEN TYPE: fpa-backend validates the Cognito ACCESS token (docs/CONTRACT.md → Auth), so that is
 * what goes out as the bearer.
 *
 * Storage: the access token lives in memory only; the Cognito refresh token (and the username the
 * refresh needs for SECRET_HASH) live in sessionStorage so a reload can silently re-mint an access
 * token. Nothing touches localStorage.
 */
import {
  type AuthConfig,
  completeLogin,
  logout as sdkLogout,
  refresh as sdkRefresh,
  startLogin,
  type Tokens,
} from '@rbi/auth-web'

import { type ApiError, setAuthErrorHandler, setAuthTokenProvider } from '@/api/client'
import { env } from '@/config/env'

export const CALLBACK_PATH = '/auth/callback'
const RT_KEY = 'rbi.fpa.rt'
const UN_KEY = 'rbi.fpa.un'
const RETURN_KEY = 'rbi.fpa.return'
const REASON_KEY = 'rbi.fpa.reason'

export type SignInReason = 'SESSION_EXPIRED'

let idToken: string | null = null

function config(): AuthConfig {
  return { identityUrl: env.identityUrl, redirectUri: `${window.location.origin}${CALLBACK_PATH}` }
}

/** `cognito:username` (or `username`/email) from a JWT — unverified, only labels the refresh call. */
export function usernameFromToken(token: string): string {
  try {
    const segment = token.split('.')[1] ?? ''
    const claims = JSON.parse(atob(segment.replace(/-/g, '+').replace(/_/g, '/'))) as Record<string, unknown>
    const name = claims['cognito:username'] ?? claims.username ?? claims.email
    return typeof name === 'string' ? name : ''
  } catch {
    return ''
  }
}

function persist(tokens: Tokens): void {
  idToken = tokens.idToken
  if (tokens.refreshToken) sessionStorage.setItem(RT_KEY, tokens.refreshToken)
  const username = usernameFromToken(tokens.idToken)
  if (username) sessionStorage.setItem(UN_KEY, username)
}

function clear(): void {
  idToken = null
  sessionStorage.removeItem(RT_KEY)
  sessionStorage.removeItem(UN_KEY)
}

export function getIdToken(): string | null {
  return idToken
}

/** The signed-in email from the id token (unverified; display only), or null. */
export function signedInEmail(): string | null {
  if (!idToken) return null
  try {
    const segment = idToken.split('.')[1] ?? ''
    const claims = JSON.parse(atob(segment.replace(/-/g, '+').replace(/_/g, '/'))) as Record<string, unknown>
    return typeof claims.email === 'string' ? claims.email.toLowerCase() : null
  } catch {
    return null
  }
}

/** An in-memory id token, or a refresh token that can re-mint one. */
export function hasSession(): boolean {
  return idToken !== null || sessionStorage.getItem(RT_KEY) !== null
}

/** Why the user is looking at the sign-in screen. */
export function readSignInReason(): SignInReason | null {
  return sessionStorage.getItem(REASON_KEY) === 'SESSION_EXPIRED' ? 'SESSION_EXPIRED' : null
}

/** Forget the sign-in reason once it has been shown (kept separate so StrictMode re-renders see it). */
export function clearSignInReason(): void {
  sessionStorage.removeItem(REASON_KEY)
}

/** Redirect to identity's Hosted UI (staff SSO). The current path is restored after the callback. */
export async function beginLogin(): Promise<void> {
  const here = `${window.location.pathname}${window.location.search}`
  sessionStorage.setItem(RETURN_KEY, here.startsWith(CALLBACK_PATH) ? '/' : here)
  const url = await startLogin(config(), { provider: 'EntraID' })
  window.location.assign(url)
}

/** Where to go after a completed login (read once). Only same-origin paths are honoured. */
export function consumeReturnPath(): string {
  const path = sessionStorage.getItem(RETURN_KEY) ?? '/'
  sessionStorage.removeItem(RETURN_KEY)
  return path.startsWith('/') && !path.startsWith('//') ? path : '/'
}

let exchanging: Promise<void> | null = null

/** Second leg of the Hosted-UI flow (the SDK verifies `state`, then exchanges code + PKCE). */
export function completeLoginFromCallback(code: string, state: string | null): Promise<void> {
  exchanging = completeLogin(config(), { code, state })
    .then(persist)
    .finally(() => {
      exchanging = null
    })
  return exchanging
}

let refreshing: Promise<boolean> | null = null

/** Single-flight silent refresh. Clears the session when it fails. */
export function refreshSession(): Promise<boolean> {
  refreshing ??= runRefresh().finally(() => {
    refreshing = null
  })
  return refreshing
}

async function runRefresh(): Promise<boolean> {
  const refreshToken = sessionStorage.getItem(RT_KEY)
  if (!refreshToken) return false
  try {
    const tokens = await sdkRefresh(config(), refreshToken, sessionStorage.getItem(UN_KEY) ?? undefined)
    idToken = tokens.idToken
    if (tokens.refreshToken) sessionStorage.setItem(RT_KEY, tokens.refreshToken)
    return true
  } catch {
    clear()
    return false
  }
}

/** Per-app sign-out: revokes the refresh token at identity (best-effort) and clears local state. */
export async function signOut(): Promise<void> {
  const refreshToken = sessionStorage.getItem(RT_KEY)
  clear()
  try {
    await sdkLogout(config(), refreshToken)
  } catch {
    // best-effort; local state is already cleared
  }
}

async function provideToken(): Promise<string | null> {
  if (exchanging) await exchanging.catch(() => undefined)
  if (idToken === null && sessionStorage.getItem(RT_KEY) !== null) await refreshSession()
  return idToken
}

let redirecting = false

function onUnauthorized(_error: ApiError): void {
  if (window.location.pathname.startsWith(CALLBACK_PATH)) return
  void refreshSession().then((ok) => {
    if (ok || redirecting) return
    redirecting = true
    sessionStorage.setItem(REASON_KEY, 'SESSION_EXPIRED')
    // Reload into the sign-in screen (no session left), keeping the current path for the return.
    window.location.assign(`${window.location.pathname}${window.location.search}`)
  })
}

let wired = false

/** Wire the API client to this module. Idempotent; call once at startup. */
export function wireIdentityAuth(): void {
  if (wired) return
  wired = true
  setAuthTokenProvider(provideToken)
  setAuthErrorHandler(onUnauthorized)
}
